import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useActiveBrand } from "../hooks/useActiveBrand";
import { useBrandReview } from "../hooks/useBrandReview";
import {
  useBriefDocument,
  useDeleteBrief,
  retryNotice,
  useRetryBrief,
  useSaveBriefDocument,
  useSkipBrief,
} from "../hooks/useCampaigns";
import { Badge } from "../components/ui/Badge";
import { CopyButton } from "../components/ui/CopyButton";
import { Card, CardHeader, CardTitle } from "../components/ui/Card";
import { OperatorBrandGate } from "../components/layout/OperatorBrandGate";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { ApproveAndPostLinkedIn } from "../components/review/ApproveAndPostLinkedIn";
import { ApproveAndPostYouTube } from "../components/review/ApproveAndPostYouTube";
import { ManualDraftForm } from "../components/drafts/ManualDraftForm";
import { ReviewQueueRow } from "../components/review/ReviewQueueRow";
import { AttributionWidget } from "../components/review/AttributionWidget";
import { ApprovedDraftsList } from "../components/review/ApprovedDraftsList";
import { ApprovalConfirmation, type ApprovedNotice } from "../components/review/ApprovalConfirmation";
import { surfaceLabel } from "../lib/campaign-surfaces";
import { buildBrandScopedPath } from "../lib/brand-navigation";
import { isDraftThisBrief } from "../lib/draft-this";
import type { Brief, ReviewApprovalItem, ReviewTrustAlert } from "../../../shared/types";
import {
  isDismissibleReviewAlert,
  reviewTrustDismissKey,
  reviewTrustExplanation,
  reviewTrustOperatorActionText,
} from "../../../shared/review-source-risk";


const EMPTY_APPROVALS: ReviewApprovalItem[] = [];
const TRUST_ALERT_DISMISS_KEY_PREFIX = "hc.review.dismissed-trust-alerts:";

function decodeHashTarget(value: string): string | null {
  if (!value) return null;
  const raw = value.slice(1);
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function formatShortDate(date: string | null | undefined): string {
  if (!date) return "Unscheduled";
  return new Date(date).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function safeExternalUrl(rawValue: string | null | undefined): string | null {
  if (!rawValue?.trim()) return null;
  try {
    const parsed = new URL(rawValue);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function buildPerformanceFollowupPrompt(brief: Brief): string | null {
  if (!brief.published_url?.trim()) return null;
  const surface = brief.surface ?? brief.platform ?? "content";
  return [
    `Review the published ${surface} item "${brief.title}".`,
    `Published URL: ${brief.published_url.trim()}`,
    "Capture the first performance signals or operator observations you have.",
    "Then update durable brand learnings, packet context, or future brief guidance if this result should change how we write or publish.",
  ].join("\n");
}

function PublishedFollowupCard({ brief }: { brief: Brief }) {
  const publishedHref = safeExternalUrl(brief.published_url);
  const publishedAt = brief.published_at
    ? new Date(brief.published_at).toLocaleString()
    : null;
  const performancePrompt = buildPerformanceFollowupPrompt(brief);
  const performanceFollowupHref = `/agent/${encodeURIComponent(brief.brand_id)}?new=1&briefId=${encodeURIComponent(brief.id)}`;

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold">Published URL recorded</p>
          <p className="mt-1 text-xs text-emerald-800">{brief.title}</p>
          {publishedAt ? <p className="mt-1 text-xs text-emerald-800">Recorded {publishedAt}</p> : null}
        </div>
        <Badge variant="success">Performance follow-up</Badge>
      </div>
      {publishedHref ? (
        <a href={publishedHref} target="_blank" rel="noreferrer" className="mt-3 block break-all font-medium text-emerald-900 underline">
          {brief.published_url}
        </a>
      ) : brief.published_url ? (
        <p className="mt-3 break-all font-medium text-emerald-900">{brief.published_url}</p>
      ) : null}
      <p className="mt-3 text-xs text-emerald-800">
        Next: verify the final output and start performance follow-up for this published item.
      </p>
      {performancePrompt ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <CopyButton text={performancePrompt} label="Copy follow-up prompt" />
          <a
            href={performanceFollowupHref}
            className="inline-flex items-center rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-900 hover:bg-emerald-100"
          >
            Open agent follow-up
          </a>
        </div>
      ) : null}
    </div>
  );
}

function trustAlertSeverity(alert: ReviewTrustAlert): "blocker" | "warning" | "info" {
  return alert.severity ?? ((alert.kind === "source" || alert.kind === "run") ? "blocker" : "warning");
}

function trustVariant(alert: ReviewTrustAlert) {
  const severity = trustAlertSeverity(alert);
  if (severity === "blocker") return "error";
  if (severity === "info") return "info";
  return "warning";
}

function trustSeverityLabel(alert: ReviewTrustAlert): string {
  const severity = trustAlertSeverity(alert);
  if (severity === "blocker") return "Blocker";
  if (severity === "warning") return "Warning";
  return "Info";
}

function AwaitingApprovalCard({ brief, brandId, onPublished, onApproved }: {
  brief: ReviewApprovalItem; brandId: string; onPublished: (brief: Brief) => void; onApproved: (brief: Brief) => void;
}) {
  const surface = (brief.surface ?? brief.platform ?? "").toLowerCase();
  if (surface === "blog" || surface === "website") return <ApproveAndPostLinkedIn brief={brief} brandId={brandId} onPublished={onPublished} onApproved={onApproved} />;
  if (surface.startsWith("youtube")) {
    return <ApproveAndPostYouTube brief={brief} brandId={brandId} onPublished={onPublished} onApproved={onApproved} />;
  }
  return <ApproveAndPostLinkedIn brief={brief} brandId={brandId} onPublished={onPublished} onApproved={onApproved} />;
}

function PacketContextCard({ packet }: { packet: NonNullable<ReviewApprovalItem["content_packet"]> }) {
  return (
    <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-3 text-sm text-blue-950">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-blue-800 ring-1 ring-inset ring-blue-200">
          Context packet
        </span>
        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200">
          {packet.status.replace(/_/g, " ")}
        </span>
        {packet.source_refs.length > 0 && (
          <span className="text-xs text-slate-600">{packet.source_refs.length} source ref{packet.source_refs.length === 1 ? "" : "s"}</span>
        )}
      </div>
      {packet.angle_brief && <p className="mt-2"><span className="font-semibold">Angle brief:</span> {packet.angle_brief}</p>}
      {packet.review_report && <p className="mt-2"><span className="font-semibold">Review report:</span> {packet.review_report}</p>}
      {packet.approval_packet && <p className="mt-2"><span className="font-semibold">Approval packet:</span> {packet.approval_packet}</p>}
    </div>
  );
}

function reviewNextAction({
  awaitingCount,
  stuckCount,
  trustAlertCount,
}: {
  awaitingCount: number;
  stuckCount: number;
  trustAlertCount: number;
}) {
  if (awaitingCount > 0) return "Review approval-ready drafts.";
  if (stuckCount > 0) return "Resolve blocked briefs.";
  if (trustAlertCount > 0) return "Check trust alerts before approving this draft.";
  return "No active operator work.";
}

function approvalDeskBadge({
  awaitingCount,
  stuckCount,
  trustAlertCount,
}: {
  awaitingCount: number;
  stuckCount: number;
  trustAlertCount: number;
}) {
  if (awaitingCount > 0) {
    return {
      variant: "warning" as const,
      label: `${awaitingCount} approval${awaitingCount === 1 ? "" : "s"}`,
    };
  }
  if (stuckCount > 0) return { variant: "error" as const, label: `${stuckCount} blocked` };
  if (trustAlertCount > 0) {
    return {
      variant: "warning" as const,
      label: `${trustAlertCount} trust alert${trustAlertCount === 1 ? "" : "s"}`,
    };
  }
  return { variant: "default" as const, label: "no approvals" };
}

function reviewPrimaryAction({
  brandId,
  awaitingCount,
  stuckBriefs,
  trustAlerts,
}: {
  brandId: string;
  awaitingCount: number;
  stuckBriefs: Brief[];
  trustAlerts: ReviewTrustAlert[];
}) {
  if (awaitingCount > 0) {
    return { label: "Open approval queue", href: "#awaiting-approval" };
  }
  if (stuckBriefs.length > 0) {
    return { label: "Resolve blocked briefs", href: "#stuck-briefs" };
  }
  const blockingTrustAlerts = trustAlerts.filter((alert) => trustAlertSeverity(alert) === "blocker");
  if (blockingTrustAlerts.length > 0) {
    const firstRunId = blockingTrustAlerts.find((alert) => alert.run_id)?.run_id;
    return {
      label: firstRunId ? "Inspect latest trust alert" : "Review trust alerts",
      href: firstRunId ? buildBrandScopedPath(`/runs/${firstRunId}`, brandId) : "#trust-alerts",
    };
  }
  return null;
}

function scrollToApprovalTarget(briefId: string) {
  if (typeof window === "undefined") return;
  const targetId = `brief-${briefId}`;
  window.requestAnimationFrame(() => {
    const target = document.getElementById(targetId);
    target?.scrollIntoView({ block: "start", behavior: "smooth" });
  });
}

function ReviewEmptyState({
  stuckCount,
  trustAlertCount,
}: {
  stuckCount: number;
  trustAlertCount: number;
}) {
  let message = "No briefs are currently waiting for review.";
  let nextAction = "Drafts will show up here when they are ready for review. From this page, you can inspect the draft, revise if needed, and approve when ready.";

  if (stuckCount > 0 || trustAlertCount > 0) {
    message = "No approval-ready briefs yet because review work is blocked upstream.";
    nextAction = "Resolve blocked briefs and blocker-severity trust alerts below. Once a clean draft is ready, it will appear here for review and approval.";
  }

  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-4">
      <p className="text-sm font-medium text-slate-900">{message}</p>
      <p className="mt-1 text-sm text-slate-600">{nextAction}</p>
      <p className="mt-2 text-xs text-slate-500">
        Review flow: drafts show up here, you can revise if needed, and you approve when the draft is ready.
      </p>
      <p className="mt-2 text-xs text-slate-500">
        Current blockers: {stuckCount} blocked, {trustAlertCount} trust alerts.
      </p>
    </div>
  );
}

function StuckBriefCard({ brief, brandId }: { brief: Brief; brandId: string }) {
  const draftQ = useBriefDocument(brief.campaign_id, brief.id, "draft", true);
  const saveDoc = useSaveBriefDocument(brandId);
  const retry = useRetryBrief(brief.campaign_id, brandId);
  const skip = useSkipBrief(brief.campaign_id, brandId);
  const remove = useDeleteBrief(brief.campaign_id, brandId);
  const [draft, setDraft] = useState("");
  const [draftTouched, setDraftTouched] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const isWorking = saveDoc.isPending || retry.isPending || skip.isPending || remove.isPending;
  // Retry on a Draft this brief redrafts it from its brief (lib/draft-this.ts).
  const draftThisBrief = isDraftThisBrief(brief);

  useEffect(() => {
    const content = draftQ.data?.data?.content;
    if (!draftTouched && typeof content === "string") {
      setDraft(content);
    }
  }, [draftQ.data?.data?.content, draftTouched]);

  async function saveReplacementDraft() {
    if (!draft.trim()) {
      throw new Error("Replacement draft cannot be empty");
    }
    await saveDoc.mutateAsync({
      campaignId: brief.campaign_id,
      briefId: brief.id,
      type: "draft",
      content: draft,
      metadata: {
        operator_repair: true,
        repair_source: "blocked_review_desk",
      },
    });
    setSavedNotice("Replacement draft saved.");
  }

  async function handleSaveReplacement() {
    setError(null);
    setSavedNotice(null);
    try {
      await saveReplacementDraft();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  async function handleSaveAndRetry() {
    setError(null);
    setSavedNotice(null);
    try {
      await saveReplacementDraft();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
      return;
    }
    try {
      await retry.mutateAsync(brief.id);
      setSavedNotice("Replacement draft saved and retry started.");
    } catch (err) {
      setSavedNotice("Replacement draft saved, but retry did not start.");
      setError(err instanceof Error ? err.message : "Retry failed");
    }
  }

  async function handleRetry() {
    setError(null);
    setSavedNotice(null);
    try {
      setSavedNotice(retryNotice(await retry.mutateAsync(brief.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Retry failed");
    }
  }

  async function handleSkip() {
    setError(null);
    try {
      await skip.mutateAsync({
        briefId: brief.id,
        reason: reason.trim() || "Skipped from review dashboard",
      });
      setReason("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Skip failed");
    }
  }

  async function handleDelete() {
    setError(null);
    try {
      await remove.mutateAsync(brief.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  }

  return (
    <div id={`brief-${brief.id}`} className="rounded-lg border border-red-200 bg-red-50 p-4 scroll-mt-24">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">{brief.title}</p>
          <p className="mt-1 text-xs text-slate-600">
            {surfaceLabel(brief.surface)} · {formatShortDate(brief.scheduled_at)}
          </p>
        </div>
        <Badge variant="error">{brief.state}</Badge>
      </div>

      <p className="mt-3 text-sm text-red-900">
        {brief.blocked_reason?.trim() || "No blocked reason recorded."}
      </p>

      <div className="mt-4 rounded-md border border-red-200 bg-white p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-slate-900">Replacement draft</p>
            <p className="mt-1 text-xs text-slate-500">
              Edit the saved markdown/text draft, then retry the blocked review.
            </p>
          </div>
          {draftQ.isLoading ? <Badge variant="default">Loading draft</Badge> : null}
        </div>
        {draftQ.error ? (
          <p className="mt-3 text-xs text-red-700">
            Current draft could not be loaded. You can still paste a replacement below.
          </p>
        ) : null}
        <textarea
          value={draft}
          onChange={(e) => {
            setDraftTouched(true);
            setDraft(e.target.value);
          }}
          placeholder="Paste the replacement draft here."
          className="mt-3 min-h-[220px] w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm leading-6 text-slate-900 outline-none ring-0 placeholder:text-slate-400"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleSaveReplacement}
            disabled={isWorking || !draft.trim()}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Save replacement draft
          </button>
          {/* Round-3 P0-2: Retry on a Draft this brief drafts it again from its brief, so a replacement
              typed here would be discarded. Offer Save only, and say so. */}
          {!draftThisBrief && <button
            type="button"
            onClick={handleSaveAndRetry}
            disabled={isWorking || !draft.trim()}
            className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            Save and retry
          </button>}
        </div>
        {draftThisBrief && <p className="mt-2 text-xs text-slate-600">Retry drafts this brief again from its brief; it does not submit a replacement draft.</p>}
      </div>

      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Optional note for skip"
        className="mt-3 min-h-[72px] w-full rounded-md border border-red-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-0 placeholder:text-slate-400"
      />

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleRetry}
          disabled={isWorking}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          Retry
        </button>
        <button
          type="button"
          onClick={handleSkip}
          disabled={isWorking}
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Skip
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={isWorking}
          className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Delete
        </button>
      </div>

      {savedNotice ? <p className="mt-2 text-xs text-emerald-700">{savedNotice}</p> : null}
      {error ? <p className="mt-2 text-xs text-red-700">{error}</p> : null}
    </div>
  );
}

export function BrandReview() {
  const location = useLocation();
  const consumedDeepLinkRef = useRef<string | null>(null);
  const { activeBrand, activeBrandId, isLoading: brandLoading } = useActiveBrand();
  const reviewQ = useBrandReview(activeBrandId);
  const [publishedFollowups, setPublishedFollowups] = useState<Brief[]>([]);
  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(null);
  const [showManualDraftForm, setShowManualDraftForm] = useState(false);
  const [dismissedTrustAlertIds, setDismissedTrustAlertIds] = useState<string[]>([]);
  // Approved drafts leave the queue at once: confirm the approval and keep them findable under Approved.
  const [approvedNotice, setApprovedNotice] = useState<ApprovedNotice | null>(null);
  const [queueView, setQueueView] = useState<"needs_approval" | "approved">(
    () => new URLSearchParams(location.search).get("view") === "approved" ? "approved" : "needs_approval",
  );

  useEffect(() => {
    setPublishedFollowups([]);
    setApprovedNotice(null);
  }, [activeBrandId]);

  useEffect(() => {
    setShowManualDraftForm(false);
  }, [activeBrandId]);

  useEffect(() => {
    if (!activeBrandId || typeof window === "undefined") {
      setDismissedTrustAlertIds([]);
      return;
    }
    try {
      const raw = window.localStorage.getItem(`${TRUST_ALERT_DISMISS_KEY_PREFIX}${activeBrandId}`);
      const parsed = raw ? JSON.parse(raw) : [];
      setDismissedTrustAlertIds(Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : []);
    } catch {
      setDismissedTrustAlertIds([]);
    }
  }, [activeBrandId]);

  useEffect(() => {
    if (!activeBrandId || typeof window === "undefined") return;
    window.localStorage.setItem(
      `${TRUST_ALERT_DISMISS_KEY_PREFIX}${activeBrandId}`,
      JSON.stringify(dismissedTrustAlertIds),
    );
  }, [activeBrandId, dismissedTrustAlertIds]);

  useEffect(() => {
    if (!reviewQ.data?.data) return;
    const deepLinkKey = `${location.key}:${location.search}:${location.hash}`;
    if (consumedDeepLinkRef.current === deepLinkKey) return;
    const briefIdFromSearch = new URLSearchParams(location.search).get("briefId");
    const targetId = location.hash
      ? decodeHashTarget(location.hash)
      : briefIdFromSearch
        ? `brief-${briefIdFromSearch}`
        : null;
    if (!targetId) return;

    const maxWaitMs = 3000;
    const startedAt = window.performance.now();
    let retryTimer: number | null = null;
    let frame: number | null = null;

    const retry = () => {
      if (window.performance.now() - startedAt > maxWaitMs) return;
      retryTimer = window.setTimeout(resolveTarget, 100);
    };

    const resolveTarget = () => {
      const target = document.getElementById(targetId);
      if (!target) {
        retry();
        return;
      }

      const beforeTop = target.getBoundingClientRect().top + window.scrollY;

      frame = window.requestAnimationFrame(() => {
        const afterTop = target.getBoundingClientRect().top + window.scrollY;
        if (Math.abs(afterTop - beforeTop) > 1) {
          retry();
          return;
        }

        target.scrollIntoView({ block: "start" });
        consumedDeepLinkRef.current = deepLinkKey;
      });
    };

    resolveTarget();
    return () => {
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [location.hash, location.key, location.search, reviewQ.data?.data]);

  const awaitingApproval = reviewQ.data?.data?.awaiting_approval ?? EMPTY_APPROVALS;
  useEffect(() => {
    const briefIdFromSearch = new URLSearchParams(location.search).get("briefId");
    const briefIdFromHash = location.hash
      ? decodeHashTarget(location.hash)?.replace(/^brief-/, "") ?? null
      : null;
    const targetBriefId = briefIdFromSearch ?? briefIdFromHash;

    setSelectedApprovalId((current) => {
      if (targetBriefId && awaitingApproval.some((brief) => brief.id === targetBriefId)) return targetBriefId;
      if (current && awaitingApproval.some((brief) => brief.id === current)) return current;
      return awaitingApproval[0]?.id ?? null;
    });
  }, [awaitingApproval, location.hash, location.search]);

  const selectedApproval = awaitingApproval.find((brief) => brief.id === selectedApprovalId) ?? awaitingApproval[0] ?? null;
  const dashboard = reviewQ.data?.data;
  const stuckBriefs = dashboard?.stuck_briefs ?? [];
  const trustAlerts = dashboard?.trust_alerts ?? [];

  useEffect(() => {
    if (dismissedTrustAlertIds.length === 0) return;
    setDismissedTrustAlertIds((current) => {
      let changed = false;
      const migrated = current.flatMap((value) => {
        if (value.includes("|")) return [value];
        const matchingAlert = trustAlerts.find((alert) => alert.id === value);
        changed = true;
        return matchingAlert ? [reviewTrustDismissKey(matchingAlert)] : [];
      });
      const deduped = Array.from(new Set(migrated));
      if (!changed && deduped.length === current.length && deduped.every((value, index) => value === current[index])) {
        return current;
      }
      return deduped;
    });
  }, [dismissedTrustAlertIds, trustAlerts]);

  if (brandLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!activeBrandId || !activeBrand) {
    return (
      <OperatorBrandGate
        title="Choose a brand before reviewing content"
        description="Review actions are brand-scoped. Select the brand you intend to approve, revise, or debug before opening the operator queue."
      />
    );
  }

  if (reviewQ.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (reviewQ.error) {
    return (
      <Card className="border-red-200 bg-red-50 text-red-800">
        {reviewQ.error instanceof Error ? reviewQ.error.message : "Failed to load review dashboard"}
      </Card>
    );
  }

  const visibleTrustAlerts = trustAlerts.filter((alert) => {
    if (trustAlertSeverity(alert) === "blocker") return true;
    const dismissKey = reviewTrustDismissKey(alert);
    return !dismissedTrustAlertIds.includes(dismissKey);
  });
  const blockingTrustAlerts = visibleTrustAlerts.filter((alert) => trustAlertSeverity(alert) === "blocker");
  const warningTrustAlerts = visibleTrustAlerts.filter((alert) => trustAlertSeverity(alert) === "warning");
  const infoTrustAlerts = visibleTrustAlerts.filter((alert) => trustAlertSeverity(alert) === "info");
  const approvalDeskBadgeState = approvalDeskBadge({
    awaitingCount: awaitingApproval.length,
    stuckCount: stuckBriefs.length,
    trustAlertCount: blockingTrustAlerts.length,
  });
  const primaryAction = reviewPrimaryAction({
    brandId: activeBrandId,
    awaitingCount: awaitingApproval.length,
    stuckBriefs,
    trustAlerts: blockingTrustAlerts,
  });

  function handlePublishedBrief(brief: Brief) {
    setPublishedFollowups((current) => [brief, ...current.filter((entry) => entry.id !== brief.id)].slice(0, 3));
  }

  function handleApprovedBrief(brief: Brief) {
    setApprovedNotice({ id: brief.id, title: brief.title, campaign_id: brief.campaign_id });
  }

  function handleDismissTrustAlert(alert: ReviewTrustAlert) {
    const dismissKey = reviewTrustDismissKey(alert);
    setDismissedTrustAlertIds((current) => (current.includes(dismissKey) ? current : [...current, dismissKey]));
  }

  function handleSelectApproval(briefId: string) {
    setSelectedApprovalId(briefId);
    scrollToApprovalTarget(briefId);
  }

  return (
    <div className="space-y-6">
      <WorkspaceHeader
        compact
        eyebrow="Review"
        title={`${activeBrand.name} Review`}
        description="Read a draft, check its sources, then approve or request edits."
        breadcrumbs={[
          { label: "Workspace", href: "/home" },
          { label: "Review" },
        ]}
        badges={(
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
            {awaitingApproval.length} approval{awaitingApproval.length === 1 ? "" : "s"}
          </span>
        )}
        headingActions={(
          <button
            type="button"
            onClick={() => setShowManualDraftForm((current) => !current)}
            className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            {showManualDraftForm ? "Hide manual draft" : "New draft"}
          </button>
        )}
      />

      {showManualDraftForm ? (
        <ManualDraftForm
          brandId={activeBrandId}
          onCancel={() => setShowManualDraftForm(false)}
        />
      ) : null}

      {approvedNotice ? (
        <ApprovalConfirmation
          brandId={activeBrandId}
          approved={approvedNotice}
          onViewApproved={() => setQueueView("approved")}
          onDismiss={() => setApprovedNotice(null)}
        />
      ) : null}

      <div id="awaiting-approval">
        <Card className="rounded-[20px] border border-slate-200 bg-white shadow-sm">
          <div role="group" aria-label="Review filter" className="mb-4 flex flex-wrap gap-2">
            {([["needs_approval", `Needs approval (${awaitingApproval.length})`], ["approved", "Approved"]] as const).map(([view, label]) => (
              <button
                key={view}
                type="button"
                aria-pressed={queueView === view}
                onClick={() => setQueueView(view)}
                className={`min-h-11 rounded-full border px-4 text-sm font-medium ${queueView === view ? "border-brand-600 bg-brand-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"}`}
              >
                {label}
              </button>
            ))}
          </div>
          {queueView === "approved" ? <ApprovedDraftsList brandId={activeBrandId} /> : <>
          {awaitingApproval.length > 1 ? (
            <CardHeader>
              <CardTitle>Choose a draft</CardTitle>
            </CardHeader>
          ) : null}
          <div className="space-y-4">
            {awaitingApproval.length > 0 ? (
              <div className={awaitingApproval.length > 1 ? "grid gap-4 lg:grid-cols-[minmax(260px,0.82fr),minmax(0,1.4fr)] lg:items-start" : "space-y-4"}>
                {awaitingApproval.length > 1 ? <div className="space-y-2 lg:max-h-[calc(100vh-18rem)] lg:overflow-y-auto lg:pr-1">
                  {awaitingApproval.map((brief) => (
                    <ReviewQueueRow
                      key={brief.id}
                      brief={brief}
                      isSelected={brief.id === selectedApproval?.id}
                      onSelect={() => handleSelectApproval(brief.id)}
                    />
                  ))}
                </div> : null}
                <div className="min-w-0 lg:sticky lg:top-24">
                  {selectedApproval ? (
                    <div id={`brief-${selectedApproval.id}`} className="scroll-mt-24">
                      {selectedApproval.content_packet ? <PacketContextCard packet={selectedApproval.content_packet} /> : null}
                      <AwaitingApprovalCard brief={selectedApproval} brandId={activeBrandId} onPublished={handlePublishedBrief} onApproved={handleApprovedBrief} />
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <ReviewEmptyState
                stuckCount={stuckBriefs.length}
                trustAlertCount={blockingTrustAlerts.length}
              />
            )}
          </div>
          </>}
        </Card>
      </div>

      <details className="rounded-[20px] border border-slate-200 bg-white p-4" open={blockingTrustAlerts.length > 0 || stuckBriefs.length > 0 || awaitingApproval.length === 0 || ["#stuck-briefs", "#trust-alerts"].includes(location.hash)}>
        <summary className="cursor-pointer text-sm font-semibold text-slate-700">Other brand work and schedules{blockingTrustAlerts.length > 0 ? ` · ${blockingTrustAlerts.length} source blockers` : ""}{stuckBriefs.length > 0 ? ` · ${stuckBriefs.length} stuck briefs` : ""}</summary>
        <div className="mt-4 space-y-4">
      <Card className="rounded-[20px] border-slate-900 bg-slate-950 text-white shadow-sm">
        <CardHeader className="mb-3 items-start gap-3 sm:flex-row sm:items-center">
          <div>
            <CardTitle className="text-white">Work status</CardTitle>
            <p className="mt-1 text-xs text-slate-300 sm:text-sm">
              Start here: approve ready drafts, unblock stuck work, then review source-risk alerts.
            </p>
          </div>
          <Badge variant={approvalDeskBadgeState.variant}>{approvalDeskBadgeState.label}</Badge>
        </CardHeader>
        <div className="grid gap-2 lg:grid-cols-[minmax(0,1.5fr),repeat(2,minmax(0,0.72fr))]">
          <div className="rounded-xl border border-white/10 bg-white/10 px-3 py-3">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Next action</p>
            <p className="mt-1.5 text-sm font-semibold text-white">
              {reviewNextAction({
                awaitingCount: awaitingApproval.length,
                stuckCount: stuckBriefs.length,
                trustAlertCount: blockingTrustAlerts.length,
              })}
            </p>
            {primaryAction ? (
              primaryAction.href.startsWith("#") ? (
                <a
                  href={primaryAction.href}
                  className="mt-2 inline-flex items-center rounded-full border border-white/15 bg-white px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-slate-100"
                >
                  {primaryAction.label}
                </a>
              ) : (
                <Link
                  to={primaryAction.href}
                  className="mt-2 inline-flex items-center rounded-full border border-white/15 bg-white px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-slate-100"
                >
                  {primaryAction.label}
                </Link>
              )
            ) : (
              <p className="mt-2 text-xs text-slate-400">No operator action is required right now.</p>
            )}
          </div>
          <div className="rounded-xl border border-white/10 bg-white/10 px-3 py-3">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Blocked work</p>
            <p className="mt-1 text-2xl font-semibold text-white">{stuckBriefs.length}</p>
            <p className="mt-1 text-xs text-slate-400">
              {stuckBriefs.length > 0 ? "Needs repair before approval." : "No blocked briefs."}
            </p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/10 px-3 py-3">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Source-risk alerts</p>
            <p className="mt-1 text-2xl font-semibold text-white">{blockingTrustAlerts.length}</p>
            {warningTrustAlerts.length + infoTrustAlerts.length > 0 ? (
              <p className="mt-1 text-xs text-slate-400">
                {warningTrustAlerts.length} warning{warningTrustAlerts.length === 1 ? "" : "s"} · {infoTrustAlerts.length} info
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-400">No extra context notes.</p>
            )}
          </div>
        </div>
      </Card>

      {publishedFollowups.length > 0 ? (
        <Card className="rounded-[20px] border border-emerald-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle>Published Follow-up</CardTitle>
            <Badge variant="success">{publishedFollowups.length}</Badge>
          </CardHeader>
          <div className="space-y-3">
            {publishedFollowups.map((brief) => (
              <PublishedFollowupCard key={brief.id} brief={brief} />
            ))}
          </div>
        </Card>
      ) : null}

      <Card className="rounded-[20px] border border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <CardTitle>Attribution</CardTitle>
        </CardHeader>
        <AttributionWidget brandId={activeBrandId} />
      </Card>

      <div className="space-y-4">
        <details id="stuck-briefs" className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm" open={stuckBriefs.length > 0 || location.hash === "#stuck-briefs"}>
            <summary className="cursor-pointer list-none">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Blocked work</p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-900">Resolve blocked briefs</h3>
                </div>
                <Badge variant="error">{stuckBriefs.length}</Badge>
              </div>
            </summary>
            <div className="mt-3 space-y-3">
              {stuckBriefs.length > 0 ? (
                stuckBriefs.map((brief) => (
                  <StuckBriefCard key={brief.id} brief={brief} brandId={activeBrandId} />
                ))
              ) : (
                <p className="text-sm text-slate-500">No blocked briefs right now.</p>
              )}
            </div>
        </details>

        <details id="trust-alerts" className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm" open={blockingTrustAlerts.length > 0 || location.hash === "#trust-alerts"}>
            <summary className="cursor-pointer list-none">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Review context</p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-900">Trust Alerts</h3>
                </div>
                <Badge variant={blockingTrustAlerts.length > 0 ? "error" : visibleTrustAlerts.length > 0 ? "info" : "default"}>
                  {blockingTrustAlerts.length > 0
                    ? `${blockingTrustAlerts.length} blocker${blockingTrustAlerts.length === 1 ? "" : "s"}`
                    : visibleTrustAlerts.length > 0
                      ? `${visibleTrustAlerts.length} note${visibleTrustAlerts.length === 1 ? "" : "s"}`
                      : "0"}
                </Badge>
              </div>
            </summary>
            <div className="mt-3 space-y-3">
              {visibleTrustAlerts.length > 0 ? (
                visibleTrustAlerts.map((alert) => (
                  <div key={alert.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant={trustVariant(alert)}>{trustSeverityLabel(alert)}</Badge>
                          <Badge variant="default">{alert.kind}</Badge>
                          <p className="text-sm font-medium text-slate-900">{alert.title}</p>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">{reviewTrustExplanation(alert)}</p>
                        <p className="mt-2 text-xs text-slate-500">{reviewTrustOperatorActionText(alert)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {isDismissibleReviewAlert(alert) ? (
                          <button
                            type="button"
                            onClick={() => handleDismissTrustAlert(alert)}
                            className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                          >
                            Dismiss
                          </button>
                        ) : null}
                        {alert.run_id ? (
                          <Link to={buildBrandScopedPath(`/runs/${alert.run_id}`, activeBrandId)} className="text-xs font-medium text-brand-700 hover:text-brand-800 hover:underline">
                            Inspect run
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">No visible trust alerts in the latest review window.</p>
              )}
            </div>
        </details>

      </div>
        </div>
      </details>
    </div>
  );
}
